import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Pagination } from '@mui/material';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Breadcrumbs,
  CardComponent,
  CareQualityMeasuresChart,
  Columns,
  CostTrend,
  DashedStandardDropdown,
  DottedText,
  NoDataVisual,
  OverviewCard,
  PageTitleAndSubTitle,
  Table,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { CareQualityMeasuresChartProps } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/CareQualityMeasuresChart';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { downloadPdf } from '../../core-utils/helper-utils/downloadPdfHelper';
import {
  commasForNumbers,
  formatDate,
} from '../../core-utils/helper-utils/helper';
import {
  CostTrendChartType,
  deductibleMemberDetails,
  getCareQualityMeasures,
  getCostTrend,
  getMemberData,
  getMembersDetails,
  getPMPMandPEPM,
  getTotalCost,
} from '../../core-utils/helper-utils/memberSummaryHelper';
import { getRecommendationsData } from '../../core-utils/helper-utils/recommendationsTabHelper';
import {
  PEPM,
  PMPM,
  TotalCost,
  CostValues,
  MemberValues,
  CareQualityMemberData,
} from '../../core-utils/helper-utils/summaryReportHelper';
import {
  COMMON_TEXTS,
  scrollBarStyles,
} from '../../core-utils/messages/CommonConstants';
import { membersSummaryPageMessages } from '../../core-utils/messages/membersSummaryPageMessages';
import {
  CARE_QUALITY,
  MEMBER_SUMMARY,
  PATIENT_DETAILS,
  REPORT_PLAN,
  STOPLOSS_TRIGGERS,
} from '../../core-utils/routes';
import { useStores } from '../../stores';
import Loader from '../../_components/molecules/Loader';
import TabSelection from '../../_components/molecules/TabSelection';
import TopConditionsTable from '../../_components/molecules/TopConditionsTable';
import RecommendationsTab from '../../_components/organisms/RecommendationsTab';
import { pageScrollDown } from '../SummaryPage';
import { getCohortValue } from '../../core-utils/helper-utils/overviewCardHelper';
import OverviewCardTooltipContent from '../../_components/atoms/OverviewCardTooltipContent';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import componentConstants from '../../core-utils/messages/componentConstants';
import type { CSSObject } from 'tss-react';
import EmptyData from '../../_components/atoms/Icons/EmptyData';
import { getTextWithToolTip } from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import EditDurationModal from '../../_components/molecules/EditDurationModal';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import { defaultRiskPatientColumns } from '../../core-utils/helper-utils/customColumnsHelper';

export interface MemberSummaryPageProps {}

export const getTruncatedText = (
  label: string,
  width: string,
  styles?: CSSObject,
) => {
  const useStyles = makeStyles()({
    ellipses: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      width: width,
      whiteSpace: 'nowrap',
      '&:hover': {
        overflow: 'visible',
        whiteSpace: 'normal',
        height: 'auto',
      },
      ...(styles ?? {}),
    },
  });

  const { classes } = useStyles();
  return (
    <Typography
      fontSize="14px"
      fontType="regular"
      lineHeight="24px"
      textColor={patientAccessTheme.palette.text.mediumemphasis}
      className={classes.ellipses}
    >
      {label}
    </Typography>
  );
};

const MemberSummaryPage: React.FC<MemberSummaryPageProps> = observer(() => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
    },
    scroll: scrollBarStyles,
    emptyDataIconGrid: {
      marginTop: '200px',
      marginBottom: '24px',
    },
  });

  const { classes } = useStyles();

  const memberSummaryStore = useStores().memberSummaryStore;
  const summaryCardsStore = useStores().summaryCardsStore;
  const messages = membersSummaryPageMessages.MEMBERS_PAGE;
  const [memberIds, setMemberIds] = useState<string[]>(() => {
    return toJS(memberSummaryStore.memberIds);
  });

  const employerGroupStore = useStores().employerGroupStore;

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [areDatesReady, setAreDatesReady] = useState<boolean>(false);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const requestBody1 = {
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberIds,
  };
  const requestBody2 = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberIds,
    claim_id_list: [],
    max_claim_date:
      employerGroupStore?.employerGroupResponseData?.max_claim_date,
  };

  const requestBody4 = {
    startDate: startDate,
    endDate: endDate,
    eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
    member_unique_id_list: memberIds,
    claim_id_list: [],
    rule_ids: [],
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [costValues, setCostValues] = useState<CostValues>({
    rx: '$0.00',
    medical: '$0.00',
    total: '$0.00',
  });
  const [memberValues, setMemberValues] = useState<MemberValues>({
    members: '0',
    employees: '0',
    dependents: '0',
  });
  const [totalCost, setTotalCost] = useState<TotalCost>({
    totalCost: '',
    totalMedical: '',
    totalRx: '',
  });
  const [pmpm, setPMPM] = useState<PMPM>({
    pmpm: '',
    pmpmPercentage: '',
    increment: true,
  });
  const [pepm, setPEPM] = useState<PEPM>({
    pepm: '',
    pepmPercentage: '',
    increment: true,
  });
  const [memberData, setMemberData] = useState<CareQualityMemberData>({
    totalMetUnmet: 0,
    totalMet: 0,
    totalUnmet: 0,
    totalEnrolles: 0,
  });
  const [
    careQualityMeasuresData,
    setCareQualityMeasuresData,
  ] = useState<CareQualityMeasuresChartProps>({
    categories: [],
    metPopulationData: [],
    unMetPopulation: [],
  });
  const [
    CostTrendChartType,
    setCostTrendChartType,
  ] = useState<CostTrendChartType>({
    medicalClaimsData: [],
    medicalTotalPaid: [],
    rxTotalPaid: [],
    rxClaims: [],
    xAxis: [],
  });

  const isStoplossPage = memberSummaryStore.isStoplossPage;
  const [disease, setDisease] = useState<string>(
    memberSummaryStore.currentDisease,
  );
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(
    false,
  );
  const [recommendationFetched, setRecommendationFetched] = useState<boolean>(
    false,
  );
  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
    if (!recommendationFetched) {
      getRecommendationsData(
        employerGroupStore?.employerGroupResponseData?.eg_nid,
        recommendedTabStore?.categoryToBeCalled,
        recommendedTabStore,
        setRecommendationLoading,
      ),
        setRecommendationFetched(true);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const valueFromHistoryState: any = location.state;
  const reportPlanPage =
    !isStoplossPage &&
    !valueFromHistoryState?.highCostClaimants &&
    !valueFromHistoryState?.isCareQualityPage;

  useEffect(() => {
    if (valueFromHistoryState?.isCareQualityPage) {
      setMemberIds(valueFromHistoryState.memberIds);
      return;
    }

    if (reportPlanPage) {
      const breadCrumbValue: any = location.state;
      const path: string = breadCrumbValue?.specificDeductable;
      const pathDetails: any[] = [];
      const summaryLabel: string =
        '50-50 Report Plan - ' +
        breadCrumbValue?.specificDeductable?.split('$', 1)[0] +
        '$' +
        commasForNumbers(breadCrumbValue?.amount) +
        ' specific deductible';
      memberSummaryStore.setSummaryLabel(summaryLabel);
      pathDetails.push(
        [
          'text',
          '50-50 Report Plan',
          {
            color: patientAccessTheme.palette.primary[500],
            fontWeight: 600,
          },
          () => {
            handleBackForReportPlanPage();
          },
        ],
        [
          'text',
          '50-50 Report Plan - ' +
            '$' +
            commasForNumbers(breadCrumbValue?.amount) +
            ' specific deductible',
          {
            color: patientAccessTheme.palette.primary[500],
            fontWeight: 600,
          },
          () => {
            memberSummaryStore.removeAllData();
            navigate(REPORT_PLAN, {
              state: {
                specificDeductableAmount: breadCrumbValue?.amount,
              },
            });
          },
        ],
        [
          'text',
          path,
          {
            color: patientAccessTheme.palette.text.highemphasis,
            fontWeight: 600,
          },
        ],
      );

      memberSummaryStore.setPathDetails(pathDetails);
    }
  }, []);

  const handleDateChange = () => {
    setStartDate(employerGroupStore?.selectedDuration?.startDate);
    setEndDate(employerGroupStore?.selectedDuration?.endDate);
    setAreDatesReady((prev) => !prev);
  };

  const recommendedTabStore = useStores().recommendedTabStore;

  const getAllCharts = async () => {
    setLoading(true);
    Boolean(startDate) && Boolean(endDate)
      ? await Promise.all([
          getTotalCost(
            requestBody2,
            setTotalCost,
            setCostValues,
            setMemberValues,
          ),
          getPMPMandPEPM(requestBody2, setPMPM, setPEPM),
          getCostTrend(requestBody2, setCostTrendChartType),
          getCareQualityMeasures(requestBody1, setCareQualityMeasuresData),

          getMemberData(
            { ...requestBody4, rule_ids: [valueFromHistoryState?.ruleId] },
            setMemberData,
          ),
        ])
      : '';
    setLoading(false);
  };

  const handleBack = () => {
    memberSummaryStore.removeAllData();
    navigate(STOPLOSS_TRIGGERS);
  };
  const handleBackForReportPlanPage = () => {
    memberSummaryStore.removeAllData();
    navigate(REPORT_PLAN);
  };

  useEffect(() => {
    Boolean(startDate !== '' && endDate !== '') ? getAllCharts() : '';
    isStoplossPage &&
      (memberSummaryStore.pathDetails[1][1] = messages.TITLE2 + disease);
    isStoplossPage &&
      (memberSummaryStore.pathDetails[0][3] = () => handleBack());
  }, [memberIds, areDatesReady]);

  useEffect(() => {
    setStartDate(employerGroupStore?.selectedDuration?.startDate);
    setEndDate(employerGroupStore?.selectedDuration?.endDate);
    setAreDatesReady((prev) => !prev);
  }, []);

  const handleTableMemberOnClick = (memberId: string) => {
    navigate(PATIENT_DETAILS, {
      state: {
        memberId: memberId,
      },
    });
  };

  const MemberSummaryCards = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
      tableHeader: {
        height: '48px',
        backgroundColor: patientAccessTheme.palette.grey[100],
        borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '&:first-child': {
          paddingLeft: '32px',
        },
        textAlign: 'left',
      },
      tableRow: {
        height: '56px',
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '&:first-child': {
          paddingLeft: '32px',
        },
        '& td:first-child': {
          paddingLeft: '32px',
        },
        '& td:last-child': {
          paddingRight: '20px',
        },
      },
      headerText: {
        fontSize: '12px',
        fontHeight: '12px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.highemphasis,
      },
    });

    const { classes } = useStyles();
    const cardMessages =
      membersSummaryPageMessages.MEMBERS_PAGE.MEMBER_SUMMARY_CARDS;
    const cellStyles = {
      color: patientAccessTheme.palette.text.mediumemphasis,
      fontSize: '12px',
      fontWeight: 500,
    };

    const getMediumEmphasisText = (value: string) => {
      return (
        <Typography
          fontSize="14px"
          lineHeight="24px"
          textColor={patientAccessTheme.palette.text.mediumemphasis}
          fontType="regular"
        >
          {value}
        </Typography>
      );
    };
    const columns: Columns[] = [
      {
        name: 'SERVICE',
        align: 'left',
        cellStyles: cellStyles,
        width: '423px',
        showEllipsis: true,
        ellipsisWidth: '300px',
      },
      {
        name: 'PERCENTAGE',
        align: 'center',
        cellStyles: cellStyles,
        width: '100px',
      },
    ];

    const memberColumns = [
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '200px' }}>
            {'Member Id'}
          </Typography>
        ),
        Footer: 'Member Id',
        accessor: 'memberId',
        sticky: 'left',
        Cell: ({ value }: any) => (
          <>
            <div
              onClick={() => handleTableMemberOnClick(value)}
              style={{ fontWeight: 500 }}
            >
              <DottedText text={value} />
            </div>
          </>
        ),
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '110px' }}>
            {'Recent Visit'}
          </Typography>
        ),
        Footer: 'Recent Visit',
        accessor: 'recentVisit',
        sticky: 'left',
        width: '100px',
        Cell: ({ value }: any) => <>{getMediumEmphasisText(value)}</>,
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '175px' }}>
            {'Chronic Conditions'}
          </Typography>
        ),
        Footer: 'CHRONIC CONDITIONS',
        accessor: 'chronicConditions',
        Cell: ({ value }: any) => <>{getTextWithToolTip(value, 20)}</>,
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '175px' }}>
            {'DIAGNOSIS'}
          </Typography>
        ),
        Footer: 'DIAGNOSIS',
        accessor: 'diagnosis',
        Cell: ({ value }: any) => <>{getTextWithToolTip(value, 20)}</>,
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '210px' }}>
            {'Trigger Conditions'}
          </Typography>
        ),
        Footer: 'TRIGGER CONDITIONS',
        accessor: 'triggerConditions',
        cellType: 'TextWithIcon',
        icon: 'info',
        Cell: ({ value }: any) => <>{getTextWithToolTip(value, 20)}</>,
      },
      {
        Header: (
          <Typography
            className={classes.headerText}
            style={{ width: '95px', textAlign: 'right' }}
          >
            {'Cost'}
          </Typography>
        ),
        Footer: 'COST',
        accessor: 'cost',
        Cell: ({ value }: any) => <>{getMediumEmphasisText(value)}</>,
        width: '100px',
      },
      {
        Header: (
          <Typography
            className={classes.headerText}
            style={{ width: '155px', textAlign: 'right' }}
          >
            {'Previous Year Cost'}
          </Typography>
        ),
        Footer: 'PREVIOUS YEAR COST',
        accessor: 'previousYearCost',
        Cell: ({ value }: any) => <>{getMediumEmphasisText(value)}</>,
      },
    ];

    const [membersDetails, setMembersDetails] = useState<any>([]);
    const [memberDetailsLoading, setMemberDetailsLoading] = useState<boolean>(
      false,
    );

    const [pageSize] = useState(5);
    const [pageNumber, setPageNumber] = useState(0);

    const [totalPages, setTotalPages] = useState<number>(
      Math.ceil(memberIds?.length / 5),
    );
    const requestBody3 = {
      startYear: Number(formatDate(startDate, 'Y')),
      eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
      member_unique_id_list: memberIds,
      pageNumber: pageNumber - 1,
      pageSize: pageSize,
      sortField: 'totalCost',
      sortOrder: -1,
      category: 'chronic',
      startDate: startDate,
      endDate: endDate,
      max_claim_date:
        employerGroupStore.employerGroupResponseData.max_claim_date,
      columnList: defaultRiskPatientColumns,
      showAll: false,
      diagnosis_count: 3,
      diagnosis_sort_field: 'totalPaid',
    };

    useEffect(() => {
      setTotalPages(Math.ceil(memberIds?.length / 5));
      setPageNumber(1);
    }, [memberIds]);

    const handlePageChange = (
      event: React.ChangeEvent<unknown>,
      value: number,
    ) => {
      setPageNumber(value);
    };
    useEffect(() => {
      if (Boolean(startDate !== '' && endDate !== '')) {
        if (!pageNumber) {
          return;
        }
        reportPlanPage
          ? deductibleMemberDetails(
              {
                ...requestBody3,
                deductible_amount: memberSummaryStore.deductibleAmount,
              },
              setMembersDetails,
              setMemberDetailsLoading,
            )
          : getMembersDetails(
              requestBody3,
              setMembersDetails,
              setMemberDetailsLoading,
            );
      }
    }, [pageNumber, areDatesReady]);

    return (
      <Grid container className={classes.root}>
        <Grid style={{ display: 'flex', marginBottom: '29px' }}>
          <Grid style={{ marginRight: '24px' }}>
            {valueFromHistoryState?.isCareQualityPage ? (
              <OverviewCard
                title={cardMessages.NON_COMPLAINT}
                value={String(memberData?.totalUnmet)}
                isMultiValued={true}
                rightLabelValues={[
                  [
                    cardMessages.ELIGIBLE_POPULATION,
                    String(memberData?.totalMetUnmet),
                  ],
                  [
                    cardMessages.TOTAL_POPULATION,
                    String(summaryCardsStore.memberValues.members),
                  ],
                ]}
                rootStyles={{
                  width: 'auto',
                }}
                labelsMinWidth={'110px'}
              />
            ) : (
              <OverviewCardTooltipContent
                type={'member'}
                cohortMemberValues={memberValues}
                totalMemberValues={summaryCardsStore.memberValues}
                overviewCard={
                  <OverviewCard
                    title={cardMessages.MEMBERS}
                    value={getCohortValue(
                      memberValues.members,
                      summaryCardsStore.memberValues.members,
                    )}
                    rootStyles={{
                      width: '176px',
                      height: '100px',
                    }}
                  />
                }
              />
            )}
          </Grid>

          <Grid style={{ marginRight: '24px' }}>
            <OverviewCardTooltipContent
              type="cost"
              cohortCostValues={costValues}
              totalCostValues={summaryCardsStore.costValues}
              overviewCard={
                <OverviewCard
                  title={cardMessages.TOTAL_COST}
                  value={getCohortValue(
                    (totalCost?.totalCost).toString(),
                    summaryCardsStore?.totalCost?.totalCost,
                  )}
                  rootStyles={{
                    width: '216px',
                  }}
                />
              }
            />
          </Grid>

          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={cardMessages.PMPM}
              value={pmpm?.pmpm}
              rootStyles={{
                width: '176px',
                height: '100px',
              }}
            />
          </Grid>

          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={cardMessages.PEPM}
              value={pepm?.pepm}
              rootStyles={{
                width: '176px',
                height: '100px',
              }}
            />
          </Grid>
        </Grid>
        <Grid style={{ marginBottom: '28px' }}>
          <CostTrend
            medicalClaimsData={CostTrendChartType?.medicalClaimsData}
            medicalTotalPaid={CostTrendChartType?.medicalTotalPaid}
            rxTotalPaid={CostTrendChartType?.rxTotalPaid}
            rxClaims={CostTrendChartType?.rxClaims}
            cardHeight="380px"
            cardWidth="1178px"
            chartWidth="1150"
            xAxisValues={CostTrendChartType?.xAxis}
            chartSubTitle={reportPlanPage ? messages.CHART_SUBTITLE : undefined}
          />
        </Grid>
        <Grid style={{ marginBottom: '28px' }}>
          <CardComponent
            title={cardMessages.MEMBER_DETAILS.TITLE}
            subTitle={cardMessages.MEMBER_DETAILS.SUB_TITLE}
            width="1178px"
            height="510px"
            body={
              memberDetailsLoading ? (
                <Box
                  style={{
                    display: 'flex',
                    height: '340px',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularLoader />
                </Box>
              ) : membersDetails?.length > 0 ? (
                <>
                  <Grid style={{ marginTop: '14px' }}>
                    <Table
                      classNameForTableHeaderRowTop={classes.tableHeader}
                      classNameForTableDataRow={classes.tableRow}
                      classNameForTableHeader={classes.tableHeader}
                      columns={memberColumns}
                      data={membersDetails}
                      onHoverRowData={() => {}}
                      isManualPagination={false}
                      tableHeight="340px"
                      showPagination={false}
                      pageSize={pageSize}
                      controlledPageSize={pageSize}
                      isMemberTable={true}
                      controlledPageIndex={0}
                      rightAlignAmounts={[5, 6]}
                    />
                  </Grid>
                  <Grid
                    style={{
                      position: 'absolute',
                      right: '24px',
                      marginTop: '16px',
                      bottom: '16px',
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={pageNumber}
                      onChange={handlePageChange}
                    />
                  </Grid>
                </>
              ) : (
                <NoDataVisual
                  height="340px"
                  width="inherit"
                  content={'No Member Details for the Selected Category'}
                />
              )
            }
          />
        </Grid>
        {Boolean(startDate !== '' && endDate !== '') && (
          <TopConditionsTable
            startDate={startDate}
            endDate={endDate}
            memberList={memberIds}
            columns={columns}
          />
        )}
        <Grid>
          <CareQualityMeasuresChart
            metPopulationData={careQualityMeasuresData?.metPopulationData}
            unMetPopulation={careQualityMeasuresData?.unMetPopulation}
            categories={careQualityMeasuresData?.categories}
          />
        </Grid>
      </Grid>
    );
  };

  const getStoplossTitle = () => {
    const diseasesList = memberSummaryStore.diseasesList;
    const diseasesNames: string[] = [];
    diseasesList.map((item) => {
      diseasesNames.push(item.disease);
    });

    const getMemberIds = (name: string) => {
      let ids: string[] = [];
      diseasesList.map((item) => {
        if (item.disease === name) {
          ids = item.memberIds;
        }
      });
      return ids;
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          fontSize="18px"
          fontType="semiBold"
          lineHeight="21.78px"
          textColor={patientAccessTheme.palette.text.highemphasis}
          margin="0px 10px 0px 0px"
        >
          {messages.TITLE2}
        </Typography>
        <DashedStandardDropdown
          options={diseasesNames}
          displayValue={diseasesNames}
          value={disease}
          width="150px"
          handleChange={(e: any) => {
            const value = e.target.value as string;
            setDisease(value);
            const historyValues: any = location.state;
            recommendedTabStore?.setCategoryToBeCalled(
              historyValues?.categoryToBeCalled + value,
            );
            memberSummaryStore.setCurrentDisease(value);
            memberSummaryStore.setMemberIds(getMemberIds(value));
            setMemberIds(toJS(getMemberIds(value)));
            setLoading(true);
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            style: { height: '250px', width: '400px' },
            classes: { paper: classes.scroll },
          }}
        />
      </div>
    );
  };

  const getCareQualityMeasuresTitle = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          fontSize="18px"
          fontType="semiBold"
          lineHeight="21.78px"
          textColor={patientAccessTheme.palette.text.highemphasis}
          margin="0px 10px 0px 0px"
        >
          {messages.TITLE3}
        </Typography>
        <DashedStandardDropdown
          options={memberSummaryStore?.careQualityMeasuresList}
          displayValue={memberSummaryStore?.careQualityMeasuresList}
          value={valueFromHistoryState?.individualMeasure}
          width="150px"
          handleChange={(e: any) => {
            const value = e.target.value as string;

            memberSummaryStore.removePathDetails();
            const pathDetails: any[] = [];
            pathDetails.push([
              'text',
              'Care Gaps',
              {
                color: patientAccessTheme.palette.primary[500],
                fontWeight: 500,
              },
              () => navigate(CARE_QUALITY),
            ]);
            pathDetails.push([
              'text',
              'Member Summary',
              {
                color: patientAccessTheme.palette.text.highemphasis,
                fontWeight: 600,
              },
            ]);
            memberSummaryStore.setPathDetails(pathDetails);
            navigate(MEMBER_SUMMARY, {
              state: {
                isCareQualityPage: true,
                memberIds:
                  toJS(
                    memberSummaryStore?.careQualityMeasuresMap?.get(value),
                  ) || [],
                individualMeasure: value,
                ruleId:
                  toJS(
                    memberSummaryStore.careQualityMeasureRuleID.get(value),
                  ) || [],
              },
            });
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            style: { height: '600px', width: '700px' },
            classes: { paper: classes.scroll },
          }}
        />
      </div>
    );
  };

  const [loader] = useState<any>(<Loader />);

  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };
  const snackBarMessages = componentConstants.ORGANISMS.REPORTS_MODAL;

  return (
    <>
      {loading ? (
        loader
      ) : (
        <div>
          <Grid container className={classes.root}>
            <Grid style={{ marginBottom: '19px' }}>
              <Breadcrumbs itemList={memberSummaryStore.pathDetails} />
            </Grid>
            <Grid style={{ marginBottom: '36px', width: '1182px' }}>
              <PageTitleAndSubTitle
                title={
                  isStoplossPage
                    ? getStoplossTitle()
                    : valueFromHistoryState?.isCareQualityPage
                    ? getCareQualityMeasuresTitle()
                    : messages.TITLE
                }
                subTitle={
                  isStoplossPage ? messages.SUBTITLE2 : messages.SUBTITLE.PART1
                }
                subTitleStartPeriod={
                  isStoplossPage ? '' : formatDate(startDate, 'MMMM Y')
                }
                subTitleMiddleString={
                  isStoplossPage ? '' : messages.SUBTITLE.PART2
                }
                subTitleEndPeriod={
                  isStoplossPage ? '' : formatDate(endDate, 'MMMM Y') + '.'
                }
                showDownloadButton
                downloadButtonAction={() => {
                  downloadPdf(
                    'memberSummaryReport',
                    {
                      startDate,
                      endDate,
                      eg_nid:
                        employerGroupStore?.employerGroupResponseData?.eg_nid,
                      member_unique_id_list: memberIds,
                      employer:
                        employerGroupStore?.employerGroupResponseData
                          ?.employer_group_name,
                      summaryName: memberSummaryStore.summaryLabel,
                      claim_id_list: [], //TODO: Replace it from cohorts when integrated
                      isCareQuality: valueFromHistoryState?.isCareQualityPage,
                      rule_ids: [valueFromHistoryState?.ruleId],
                      category: valueFromHistoryState?.individualMeasure,
                      maxClaimDate:
                        employerGroupStore?.employerGroupResponseData
                          ?.max_claim_date,
                      deductible_amount: reportPlanPage
                        ? `${memberSummaryStore?.deductibleAmount}`
                        : '',
                    },
                    handleOpenSnackBar,
                    'open',
                  );
                  handleOpenSnackBar(
                    snackBarMessages.SNACKBAR_MESSAGES.OPEN_REPORT,
                    'info',
                    6000,
                  );
                }}
              />
              <div
                style={{
                  position: 'relative',
                  top: '-58px',
                  left: isStoplossPage
                    ? '282px'
                    : valueFromHistoryState?.isCareQualityPage
                    ? '400px'
                    : '168px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  fontSize="17px"
                  lineHeight="21px"
                  textColor={patientAccessTheme.palette.text.mediumemphasis}
                  fontType="semiBold"
                  margin="0px 8px"
                >
                  {COMMON_TEXTS.FOR}
                </Typography>
                <EditDurationModal
                  isYearGrid={false}
                  handleDateChange={handleDateChange}
                />
              </div>
              <TabSelection
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={handleSelectedIndex}
                recommendationFetched={recommendationFetched}
                margin="0px"
              />
            </Grid>
            <Grid>
              {selectedTabIndex === 0 &&
                (valueFromHistoryState?.isCareQualityPage &&
                valueFromHistoryState?.memberIds?.length === 0 ? (
                  <Grid
                    container
                    sx={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <Grid className={classes.emptyDataIconGrid}>
                      <EmptyData />
                    </Grid>
                    <Grid>
                      <Typography
                        fontSize="14px"
                        fontType="regular"
                        lineHeight="24.78px"
                        textColor={
                          patientAccessTheme.palette.text.mediumemphasis
                        }
                      >
                        {messages.emptyData}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <MemberSummaryCards />
                ))}
              {selectedTabIndex === 1 && (
                <RecommendationsTab
                  recommendationLoading={recommendationLoading}
                  isInternalPage={true}
                />
              )}
            </Grid>
            <div id="scroll-top-element" />
          </Grid>
          {pageScrollDown('scroll-top-element')}
        </div>
      )}
      <Snackbar
        {...snackbarState}
        vertical={'bottom'}
        horizontal={'right'}
        handleClose={handleCloseSnackBar}
      />
    </>
  );
});

export default MemberSummaryPage;
